import { axios } from "../api";

/*
 * The signed-in user's own sign-in bookkeeping: `last_login = now` and
 * Invited (status 2) → Active (status 1). The SERVER decides both —
 * `POST /v6/users/me/signed-in` takes no body, acts only on the token's uid,
 * needs no grant, and never moves any status but Invited
 * (aeronet-fast-api app/users/api.py `record_signed_in`).
 *
 * 🔴 NEVER GO BACK TO `PUT /v6/users/{id}` FOR THIS. That route needs the
 * user_management edit grant; a pilot has none. H201 (2026-09-28, Mustang,
 * uid 658, status 2): App PUT `{status: 1}` on every mount, each 403 became a
 * red "Permission denied" toast on the Pilots home, and the pilot stayed
 * Invited with no last_login.
 *
 * Still quiet and bounded, because it is bookkeeping the user never asked for:
 *  - `suppressErrorToast` — a failure is logged, never shown (src/api.ts
 *    per-request opt-out). A backend without the route yet (404) is the same.
 *  - once per uid per browser session — App remounts re-run the effect
 *    (`User.currentUser()` is a cached singleton), and a sign-in is recorded
 *    once, not per remount. This replaces the old `?code=` fresh-login flag,
 *    which a GCIP sign-in never set, so `last_login` was never written there.
 */

export const signInRecordedKey = (uid: number | string) => `aeronet_signin_recorded_${uid}`;

function sessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function sessionSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // storage blocked: the call may repeat on a remount, but still quietly
  }
}

/**
 * Records the sign-in for `user` once per browser session. Resolves once the
 * call settles; never rejects and never toasts.
 */
export async function recordSignIn(user: { id?: number | string } | undefined): Promise<void> {
  if (!user?.id) return;

  const key = signInRecordedKey(user.id);
  if (sessionGet(key) === "1") return;
  sessionSet(key, "1");

  try {
    await axios.post("/v6/users/me/signed-in", undefined, { suppressErrorToast: true });
  } catch (err: any) {
    // eslint-disable-next-line no-console
    console.warn("[Auth] recording the sign-in failed; no toast (H201)", {
      userId: user.id,
      status: err?.response?.status,
    });
  }
}
